import { isAfter } from "date-fns";
import * as yup from "yup";
import type {
  IndexedOutcomeForValidation,
  OutcomeForValidation,
} from "./types";
import { hasTimestamp, isValidTimestamp } from "./sharedSchema";

const hasOutcomeValues = (outcome: OutcomeForValidation): boolean =>
  Boolean(
    outcome.unit ||
    outcome.value ||
    outcome["unit-of-sale"] ||
    outcome["outcome-start-date"] ||
    outcome["outcome-end-date"],
  );

const groupOutcomesByUnit = (
  outcomes: Array<IndexedOutcomeForValidation>,
): Record<string, Array<IndexedOutcomeForValidation>> =>
  outcomes.reduce<Record<string, Array<IndexedOutcomeForValidation>>>(
    (groupedOutcomes, indexedOutcome) => {
      const unit = indexedOutcome.outcome.unit;
      if (!unit) return groupedOutcomes;

      groupedOutcomes[unit] = [
        ...(groupedOutcomes[unit] || []),
        indexedOutcome,
      ];
      return groupedOutcomes;
    },
    {},
  );

const sortOutcomesByStartDate = (
  firstOutcome: IndexedOutcomeForValidation,
  secondOutcome: IndexedOutcomeForValidation,
): number => {
  const firstStartDate =
    firstOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER;
  const secondStartDate =
    secondOutcome.outcome["outcome-start-date"] ?? Number.MAX_SAFE_INTEGER;

  if (firstStartDate !== secondStartDate)
    return firstStartDate - secondStartDate;

  return (
    (firstOutcome.outcome.outcomeCounter || 0) -
    (secondOutcome.outcome.outcomeCounter || 0)
  );
};

const doOutcomePeriodsOverlap = (
  firstOutcome: OutcomeForValidation,
  secondOutcome: OutcomeForValidation,
): boolean => {
  const firstStartDate = firstOutcome["outcome-start-date"];
  const secondStartDate = secondOutcome["outcome-start-date"];

  if (!isValidTimestamp(firstStartDate) || !isValidTimestamp(secondStartDate)) {
    return false;
  }

  const firstEndDate =
    firstOutcome["outcome-end-date"] ?? Number.MAX_SAFE_INTEGER;
  const secondEndDate =
    secondOutcome["outcome-end-date"] ?? Number.MAX_SAFE_INTEGER;

  return firstStartDate <= secondEndDate && secondStartDate <= firstEndDate;
};

export const outcomeItemSchema = yup.object().shape({
  outcomeCounter: yup.number(),
  origin: yup.mixed<"saved" | "draft">().oneOf(["saved", "draft"]).optional(),
  unit: yup
    .string()
    .test({
      name: "saved-outcome-not-empty",
      test: (_, context) => {
        const outcome = context.parent as OutcomeForValidation;

        if (outcome.origin === "saved" && !hasOutcomeValues(outcome)) {
          return context.createError({
            message: {
              message: "Saved outcomes cannot be cleared",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    })
    .test({
      name: "outcome-unit",
      test: (_, context) => {
        const outcome = context.parent as OutcomeForValidation;

        if (outcome.value && !outcome.unit) {
          return context.createError({
            message: {
              message: "Must select Unit where Value is selected",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    }),
  value: yup
    .string()
    .test({
      name: "positive-outcome-value",
      test: (value, context) => {
        if (value && parseFloat(value) <= 0) {
          const outcome = context.parent as OutcomeForValidation;
          return context.createError({
            message: {
              message: "The value can't be 0 or negative",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    })
    .test({
      name: "numeric-outcome-value",
      test: (value, context) => {
        if (value && Number.isNaN(parseFloat(value))) {
          const outcome = context.parent as OutcomeForValidation;
          return context.createError({
            message: {
              message: "Must be a number",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    })
    .test({
      name: "outcome-value-required-for-unit",
      test: (_, context) => {
        const outcome = context.parent as OutcomeForValidation;

        if (outcome.unit && !outcome.value) {
          return context.createError({
            message: {
              message: "Must select Value where Unit is selected",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    }),
  "outcome-start-date": yup
    .mixed<number>()
    .nullable()
    .test({
      name: "outcome-start-date-required",
      test: (startDateValue, context) => {
        const outcome = context.parent as OutcomeForValidation;

        if (hasOutcomeValues(outcome) && !hasTimestamp(startDateValue)) {
          return context.createError({
            message: {
              message: "Start date is required",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    })
    .test({
      name: "outcome-start-date-valid",
      test: (startDateValue, context) => {
        if (hasTimestamp(startDateValue) && !isValidTimestamp(startDateValue)) {
          const outcome = context.parent as OutcomeForValidation;
          return context.createError({
            message: {
              message: "Must be a valid date in dd/mm/yyyy",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    }),
  "outcome-end-date": yup
    .mixed<number>()
    .nullable()
    .test({
      name: "outcome-end-date-before-start",
      test: (endDateValue, context) => {
        const outcome = context.parent as OutcomeForValidation;
        const startDateValue = outcome["outcome-start-date"];

        if (
          isValidTimestamp(startDateValue) &&
          isValidTimestamp(endDateValue) &&
          endDateValue <= startDateValue
        ) {
          return context.createError({
            message: {
              message: "End date must be after start date",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    })
    .test({
      name: "outcome-end-date-valid",
      test: (endDateValue, context) => {
        if (
          hasTimestamp(endDateValue) &&
          (!isValidTimestamp(endDateValue) ||
            !isAfter(endDateValue, new Date(1970, 1, 1)))
        ) {
          const outcome = context.parent as OutcomeForValidation;
          return context.createError({
            message: {
              message: "Must be a valid date in dd/mm/yyyy",
              outcomeCounter: outcome.outcomeCounter,
            },
          });
        }

        return true;
      },
    }),
});

const getPopulatedOutcomes = (
  outcomes: Array<OutcomeForValidation>,
): Array<IndexedOutcomeForValidation> =>
  outcomes
    .map((outcome, index) => ({ outcome, index }))
    .filter(
      ({ outcome }) =>
        hasOutcomeValues(outcome) &&
        Boolean(outcome.unit) &&
        isValidTimestamp(outcome["outcome-start-date"]),
    );

export const outcomesSchema = yup
  .array()
  .of(outcomeItemSchema)
  .test({
    name: "historical-outcomes-require-end-date",
    test: (outcomes, context) => {
      if (!outcomes) return true;

      const outcomesByUnit = groupOutcomesByUnit(
        getPopulatedOutcomes(outcomes as Array<OutcomeForValidation>),
      );

      for (const sameUnitOutcomes of Object.values(outcomesByUnit)) {
        if (sameUnitOutcomes.length <= 1) continue;

        const sortedOutcomes = [...sameUnitOutcomes].sort(
          sortOutcomesByStartDate,
        );

        for (
          let outcomeIndex = 0;
          outcomeIndex < sortedOutcomes.length - 1;
          outcomeIndex += 1
        ) {
          const historicalOutcome = sortedOutcomes[outcomeIndex];
          if (historicalOutcome.outcome["outcome-end-date"]) continue;

          return context.createError({
            path: `${context.path}[${historicalOutcome.index}].outcome-end-date`,
            message: {
              message: "End date is required on historical outcomes",
              outcomeCounter: historicalOutcome.outcome.outcomeCounter,
            },
          });
        }
      }

      return true;
    },
  })
  .test({
    name: "same-unit-periods-do-not-overlap",
    test: (outcomes, context) => {
      if (!outcomes) return true;

      const outcomesByUnit = groupOutcomesByUnit(
        getPopulatedOutcomes(outcomes as Array<OutcomeForValidation>),
      );

      for (const sameUnitOutcomes of Object.values(outcomesByUnit)) {
        const sortedOutcomes = [...sameUnitOutcomes].sort(
          sortOutcomesByStartDate,
        );

        for (
          let currentIndex = 0;
          currentIndex < sortedOutcomes.length;
          currentIndex += 1
        ) {
          const currentOutcome = sortedOutcomes[currentIndex];

          for (
            let previousIndex = 0;
            previousIndex < currentIndex;
            previousIndex += 1
          ) {
            const previousOutcome = sortedOutcomes[previousIndex];
            if (
              !doOutcomePeriodsOverlap(
                previousOutcome.outcome,
                currentOutcome.outcome,
              )
            ) {
              continue;
            }

            return context.createError({
              path: `${context.path}[${currentOutcome.index}].outcome-start-date`,
              message: {
                message: "Outcomes with the same unit cannot overlap",
                outcomeCounter: currentOutcome.outcome.outcomeCounter,
              },
            });
          }
        }
      }

      return true;
    },
  })
  .test({
    name: "unit-of-sale-periods-do-not-overlap",
    test: (outcomes, context) => {
      if (!outcomes) return true;

      const selectedUnitOfSaleOutcomes = (
        outcomes as Array<OutcomeForValidation>
      )
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome["unit-of-sale"] &&
            isValidTimestamp(outcome["outcome-start-date"]),
        )
        .sort(sortOutcomesByStartDate);

      for (
        let currentIndex = 0;
        currentIndex < selectedUnitOfSaleOutcomes.length;
        currentIndex += 1
      ) {
        const currentOutcome = selectedUnitOfSaleOutcomes[currentIndex];

        for (
          let previousIndex = 0;
          previousIndex < currentIndex;
          previousIndex += 1
        ) {
          const previousOutcome = selectedUnitOfSaleOutcomes[previousIndex];
          if (
            !doOutcomePeriodsOverlap(
              previousOutcome.outcome,
              currentOutcome.outcome,
            )
          ) {
            continue;
          }

          return context.createError({
            path: `${context.path}[${currentOutcome.index}].outcome-start-date`,
            message: {
              message: "Unit of sale periods cannot overlap",
              outcomeCounter: currentOutcome.outcome.outcomeCounter,
            },
          });
        }
      }

      return true;
    },
  })
  .test({
    name: "unit-of-sale-outcomes-use-same-unit",
    test: (outcomes, context) => {
      if (!outcomes) return true;

      const selectedUnitOfSaleOutcomes = (
        outcomes as Array<OutcomeForValidation>
      )
        .map((outcome, index) => ({ outcome, index }))
        .filter(
          ({ outcome }) =>
            hasOutcomeValues(outcome) &&
            outcome["unit-of-sale"] &&
            Boolean(outcome.unit),
        )
        .sort(sortOutcomesByStartDate);

      if (selectedUnitOfSaleOutcomes.length <= 1) return true;

      const firstUnitOfSaleUnit = selectedUnitOfSaleOutcomes[0].outcome.unit;

      for (
        let outcomeIndex = 1;
        outcomeIndex < selectedUnitOfSaleOutcomes.length;
        outcomeIndex += 1
      ) {
        const currentOutcome = selectedUnitOfSaleOutcomes[outcomeIndex];
        if (currentOutcome.outcome.unit === firstUnitOfSaleUnit) continue;

        return context.createError({
          path: `${context.path}[${currentOutcome.index}].unit`,
          message: {
            message: "All unit of sale outcomes must use the same unit",
            outcomeCounter: currentOutcome.outcome.outcomeCounter,
          },
        });
      }

      return true;
    },
  });
